import { FC, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '../../shared/ui/Icon'
import { Button } from '../../shared/ui/Button'
import { CostItemSelector } from '../../features/CostItemSelector'
import { BudgetTable, BudgetTableSkeleton } from '../../features/BudgetTable'
import { useQueryParams } from '../../shared/hooks/useQueryParams'
import { useBudget, useCostItems, useIpdObjects } from '../../shared/hooks/api'
import {
  PageContainer,
  ContentCard,
  HeaderSection,
  TitleBlock,
  TitleRow,
  Title,
  Subtitle,
  ParamsSelectorWrapper,
  SummaryCards,
  TableSection,
  SectionTitle,
  ParamsSelectors,
  ErrorMessage
} from './BudgetsPage.styles'
import { colors } from '../../shared/ui/colors'
import { BudgetItem, CostItem } from '../../entities/Budget'
import {
  BudgetSummaryCard,
  BudgetSummaryCardSkeleton
} from '../../features/BudgetSummaryCard'
import { summaryItems } from './summaryItems.tsx'
import { IpdObjectSelector } from '../../features/IpdObjectSelector'
import { IpdObject } from '../../entities/IpdObject'
import { routes } from '../../shared/constants/routes'

const defaultCostItemCodes: CostItem['code'][] = ['05', '06', '12']

export const BudgetsPage: FC = () => {
  const { livingComplexId } = useParams<{ livingComplexId: string }>()
  const { getParam, setQueryParam } = useQueryParams()
  const navigate = useNavigate()
  const { data: costItems } = useCostItems()
  const { data: ipdObject } = useIpdObjects({
    livingComplexId: livingComplexId || ''
  })
  const objectId = getParam('oipd')
  const budgetItemCodes = getParam('budget-item')

  const selectedBudgetItem = useMemo(() => {
    if (!costItems || !budgetItemCodes) {
      return { codes: [], ids: [] }
    }

    const selectedCodes = budgetItemCodes
      .split(',')
      .map(value => value.trim())
      .filter(Boolean)

    const selectedIds = costItems
      .filter(item => selectedCodes.includes(item.code))
      .map(item => item.uid)

    return { codes: selectedCodes, ids: selectedIds }
  }, [costItems, budgetItemCodes])
  const selectedIpdObject = useMemo(() => {
    if (!ipdObject?.length) {
      return null
    }

    return ipdObject.filter(object => object.id === objectId)[0]
  }, [livingComplexId, ipdObject, objectId])

  const {
    data: budget,
    isLoading: isBudgetLoading,
    error: budgetError
  } = useBudget({
    objectId: objectId ? objectId : '',
    costItemStickerIds: selectedBudgetItem.ids
  })

  useEffect(() => {
    if (!budgetItemCodes?.length) {
      setQueryParam('budget-item', defaultCostItemCodes.join(','))
    }
  }, [])

  const handleIpdObjectChange = (ipdo: IpdObject) => {
    setQueryParam('oipd', ipdo.id)
  }
  const handleBudgetItemChange = (selectedCostItems: CostItem[]) => {
    const ids = selectedCostItems.map(item => item.code)
    setQueryParam('budget-item', ids.length ? ids.join(',') : null)
  }

  const handleGoToEstimatesClick = () => {
    const livingComplexName = selectedIpdObject?.name
      ? encodeURIComponent(selectedIpdObject.name)
      : ''
    const budgetItemQuery = budgetItemCodes
      ? `&budget-item=${encodeURIComponent(budgetItemCodes)}`
      : ''

    navigate(
      `${routes.costEstimates(
        livingComplexId ?? ''
      )}?oipd=${livingComplexName}${budgetItemQuery}`
    )
  }

  return (
    <PageContainer>
      <ContentCard>
        <HeaderSection>
          <TitleBlock>
            <TitleRow>
              <Icon type="budget" size={24} color={colors.black} />
              <Title>Бюджеты</Title>
            </TitleRow>
            <Subtitle>Планирование и контроль бюджета проекта</Subtitle>
          </TitleBlock>

          <ParamsSelectorWrapper>
            <ParamsSelectors>
              <IpdObjectSelector
                selectedId={objectId}
                livingComplexId={livingComplexId ?? ''}
                onChange={handleIpdObjectChange}
              />
              <CostItemSelector
                selectedCodes={selectedBudgetItem.codes}
                onChange={handleBudgetItemChange}
              />
            </ParamsSelectors>
            <Button
              variant="outline"
              onClick={handleGoToEstimatesClick}
              disabled={!objectId}
            >
              Перейти к смете&nbsp;→
            </Button>
          </ParamsSelectorWrapper>
        </HeaderSection>

        <SummaryCards>
          {isBudgetLoading
            ? summaryItems.map(summary => (
                <BudgetSummaryCardSkeleton key={summary.key} />
              ))
            : summaryItems.map(
                summary =>
                  !!budget && (
                    <BudgetSummaryCard
                      key={summary.key}
                      label={summary.label}
                      value={
                        budget?.[summary.key as keyof BudgetItem] as number
                      }
                      units={summary.units}
                    />
                  )
              )}
        </SummaryCards>

        <TableSection>
          <SectionTitle>Детализация по статьям бюджета</SectionTitle>
          {budgetError && <ErrorMessage>{budgetError.message}</ErrorMessage>}
          {isBudgetLoading ? (
            <BudgetTableSkeleton />
          ) : (
            budget && (
              <BudgetTable
                items={budget.children}
                livingComplexId={livingComplexId ?? ''}
                objectName={selectedIpdObject?.name}
              />
            )
          )}
        </TableSection>
      </ContentCard>
    </PageContainer>
  )
}
